import type {RuleSeverity} from '@github-ui/code-scanning-shared/types/rule-severity'
import {useCallback, useMemo, useState} from 'react'
import {getFindingSeverity, getFindingSource, SEVERITY_ORDER} from './entity-helpers'
import type {Finding} from './types'

export const useFilterFindings = (openFindings: Finding[], ignoredFindings: Finding[]) => {
  const [selectedVisibility, setSelectedVisibility] = useState<'open' | 'resolved'>('open')
  const [selectedSeverities, setSelectedSeverities] = useState<Set<RuleSeverity>>(() => new Set())
  const [selectedSources, setSelectedSources] = useState<Set<string>>(() => new Set())

  const allFindings = useMemo(() => [...openFindings, ...ignoredFindings], [openFindings, ignoredFindings])

  const availableSeverities = useMemo(() => {
    const severities = new Set<RuleSeverity>()
    for (const f of allFindings) {
      const s = getFindingSeverity(f)
      if (s) severities.add(s)
    }
    return SEVERITY_ORDER.filter(s => severities.has(s))
  }, [allFindings])

  const availableSources = useMemo(() => {
    const sources = new Set<string>()
    for (const f of allFindings) {
      sources.add(getFindingSource(f))
    }
    return [...sources].sort()
  }, [allFindings])

  const handleSeverityToggle = useCallback((severity: RuleSeverity) => {
    setSelectedSeverities(prev => {
      const next = new Set(prev)
      if (next.has(severity)) next.delete(severity)
      else next.add(severity)
      return next
    })
  }, [])

  const handleSourceToggle = useCallback((source: string) => {
    setSelectedSources(prev => {
      const next = new Set(prev)
      if (next.has(source)) next.delete(source)
      else next.add(source)
      return next
    })
  }, [])

  const applyFilters = useCallback(
    (items: Finding[]) => {
      return items.filter(f => {
        if (selectedSeverities.size > 0) {
          const s = getFindingSeverity(f)
          if (!s || !selectedSeverities.has(s)) return false
        }
        if (selectedSources.size > 0) {
          if (!selectedSources.has(getFindingSource(f))) return false
        }
        return true
      })
    },
    [selectedSeverities, selectedSources],
  )

  const displayedFindings = useMemo(
    () => applyFilters(selectedVisibility === 'open' ? openFindings : ignoredFindings),
    [applyFilters, selectedVisibility, openFindings, ignoredFindings],
  )

  return {
    selectedVisibility,
    handleVisibilityChange: setSelectedVisibility,
    availableSeverities,
    availableSources,
    selectedSeverities,
    selectedSources,
    handleSeverityToggle,
    handleSourceToggle,
    displayedFindings,
  }
}
